import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Button from "@components/Button/Button.tsx";
import type { GenericDialogProps } from "@components/GenericDialog/GenericDialog.types.ts";
import { SIDE_CHILD_DRAWER_Z_INDEX } from "@constants/layout.style";
import { useTranslation } from "react-i18next";

const GenericDialog: React.FC<GenericDialogProps> = ({
  open,
  title,
  content,
  cancelButtonText,
  confirmButtonText,
  handleCloseDialog,
  confirmAction,
  elementContent,
  testId,
  closeIconAction,
  maxWidth,
  isInsideDrawer,
  isSubmitting,
  zIndex,
}) => {
  const { t } = useTranslation();
  const handleCloseIconClick = () => {
    if (closeIconAction) {
      closeIconAction();
    } else if (handleCloseDialog) {
      handleCloseDialog();
    }
  };

  const handleClose = () => {
    if (handleCloseDialog) {
      handleCloseDialog();
    }
  };

  const handleConfirm = () => {
    confirmAction?.();
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="generic-dialog"
      open={open}
      fullWidth={true}
      data-testid={testId}
      maxWidth={maxWidth}
      slotProps={{
        ...(isInsideDrawer
          ? {
              paper: { sx: { zIndex: SIDE_CHILD_DRAWER_Z_INDEX + 2 } },
              backdrop: { sx: { zIndex: SIDE_CHILD_DRAWER_Z_INDEX + 1 } },
            }
          : {}),
        ...(zIndex || isInsideDrawer
          ? { root: { sx: { zIndex: zIndex ?? SIDE_CHILD_DRAWER_Z_INDEX + 1 } } }
          : {}),
      }}
    >
      <div className="p-6">
        <div className="relative">
          <DialogTitle
            data-testid={`${testId}-title`}
            sx={{ padding: 0, fontWeight: 700, marginRight: 4 }}
          >
            {title}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseIconClick}
            data-testid={`${testId}-close-button`}
            sx={(theme) => ({
              padding: 0,
              fontWeight: 700,
              color: theme.palette.secondary.main,
              position: "absolute",
              top: 4,
              right: 0,
            })}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent
          data-testid={`${testId}-content`}
          sx={{ paddingTop: 1, paddingBottom: 3, paddingX: 0 }}
        >
          {elementContent ?? <span style={{ whiteSpace: "pre-line" }}>{content}</span>}
        </DialogContent>
        <DialogActions sx={{ padding: 0 }}>
          <Button
            variant="text"
            onClick={handleClose}
            data-testid={`${testId}-cancel-button`}
            label={<span className="normal-case font-medium">{cancelButtonText}</span>}
          />
          {confirmAction && (
            <Button
              variant="contained"
              loading={isSubmitting}
              onClick={handleConfirm}
              data-testid={`${testId}-confirm-button`}
              label={
                <span className="normal-case font-medium">
                  {confirmButtonText ?? t("common.confirm")}
                </span>
              }
            />
          )}
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default GenericDialog;
